import { memo } from 'react';
import {
  RenunciationCitizenshipVM,
  ForeignState,
  ItalianState,
  ChangeRelationshipVM,
} from 'mutation/view-models/registration-citizenship';
import {
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import ArticleList from '../shared/article-list';
import Italian from '../shared/italian';
import Foreign from '../shared/foreign';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import ChangeRelationship from '../shared/change-relationship';
import { FormikErrors, FormikTouched } from 'formik';
import { ItemVM } from 'shell/view-models/item';
import DocumentActForm from 'shell/components/shared/act';
import { MotArtCitizenship } from 'shell/models/mutation/mot-art-citizenship';

interface OpenLegisProps {
  values?: RenunciationCitizenshipVM;
  onChange: (
    key: keyof RenunciationCitizenshipVM,
    value: RenunciationCitizenshipVM[keyof RenunciationCitizenshipVM]
  ) => void;
  errors?: FormikErrors<RenunciationCitizenshipVM>;
  touched?: FormikTouched<RenunciationCitizenshipVM>;
  idReason: string;
  isNationalSearch?: string;
  onChangeValues: (values: RenunciationCitizenshipVM) => void;
  disabled?: boolean;
}

const RenunciationCitizenshipComponenent = ({
  onChange,
  values,
  errors,
  touched,
  idReason,
  isNationalSearch,
  onChangeValues,
  disabled,
}: OpenLegisProps): React.ReactElement => {
  const onChangeArticleList = useEventCallback((value: ItemVM<MotArtCitizenship> | null) => {
    onChange('article', value);
  });

  const handleForeignStateListChange = useEventCallback(
    (citizenship: ForeignStateVM | null | undefined) => {
      onChange('otherCitizenship', citizenship);
    }
  );

  const onChangeForeign = useEventCallback(
    (key: keyof ForeignState, value: ForeignState[keyof ForeignState]) => {
      const newValue = {
        ...values?.foreign,
        [key]: value,
      };

      onChange('foreign', newValue);
    }
  );

  const onChangeItalian = useEventCallback(
    (key: keyof ItalianState, value: ItalianState[keyof ItalianState]) => {
      const newValue = {
        ...values?.italian,
        [key]: value,
      };

      onChange('italian', newValue);
    }
  );

  const onChangeValuesChangeRelationship = useEventCallback((value: ChangeRelationshipVM) => {
    onChange('changeRelationship', value);
  });

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Data acquisto cittadinanza"
          aria-label="Data acquisto cittadinanza"
          value={values?.dateLoss || null}
          onChange={(date) => {
            onChange('dateLoss', date);
          }}
          renderInput={(params) => (
            <PmiTextField id="dateLoss" {...params} fullWidth helperText="" disabled={disabled} />
          )}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <ForeignStateList
          label="Nuova cittadinanza"
          aria-label="Nuova cittadinanza"
          id="otherCitizenship"
          value={values?.otherCitizenship || null}
          onChange={handleForeignStateListChange}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <ArticleList
          value={values?.article?.id?.toString() || ''}
          onChange={onChangeArticleList}
          idReason={idReason}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <DatePicker
          label="Dichiarazione resa il"
          aria-label="Dichiarazione resa il"
          value={values?.dateOath || null}
          onChange={(date) => {
            onChange('dateOath', date);
          }}
          renderInput={(params) => (
            <PmiTextField id="dateOath" {...params} fullWidth helperText="" disabled={disabled} />
          )}
        />
      </Grid>
      <Grid item xs={12} md={3} style={{ display: 'flex', alignItems: 'center' }}>
        A
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Stato"
            id="isForeign"
            defaultValue={false}
            value={values?.isForeign}
            onChange={() => onChange('isForeign', !values?.isForeign)}
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Italia"
              disabled={disabled}
            />
            <FormControlLabel value="true" control={<Radio />} label="Estero" disabled={disabled} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.isForeign ? (
        <Foreign onChange={onChangeForeign} values={values?.foreign} disabled={disabled} />
      ) : (
        <Italian
          onChange={onChangeItalian}
          values={values?.italian}
          dateFilter={values?.dateOath}
          disabled={disabled}
        />
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <DocumentActForm
        values={values}
        onChange={(key, value) => {
          onChange(key, value);
        }}
        onChanges={(fields) => {
          onChangeValues(fields);
        }}
        errors={errors}
        touched={touched}
        requiredDigital={{
          documentDate: false,
        }}
        disabled={disabled}
      />
      <Grid item xs={12}>
        <ChangeRelationship
          values={values?.changeRelationship}
          onChangeValues={onChangeValuesChangeRelationship}
          isNationalSearch={isNationalSearch}
        />
      </Grid>
    </Grid>
  );
};

RenunciationCitizenshipComponenent.displayName =
  'Form Accordion Registration Citizenship - Renunciation Citizenship component';

const RenunciationCitizenship = memo(RenunciationCitizenshipComponenent);

export default RenunciationCitizenship;
